import * as React from "react";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Box, FormLabel} from "@mui/material";
import ReactQuill, {Quill} from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {useFormikContext} from "formik";
import {FormFieldProps} from "model/form";
import {useAppDispatch} from "store";
import {uploadFile} from "../../helpers/editorTest";
// @ts-ignore
import ImageUploader from "quill-image-uploader";
import "quill-image-uploader/dist/quill.imageUploader.min.css";
import {FigureBlot, FigureValue} from "./QuillFigureBlot";
import {QuillImageDialog} from "./QuillImageDialog";

if ((Quill as any).imports['modules/imageUploader'] !== ImageUploader) {
    Quill.register("modules/imageUploader", ImageUploader);
}

// Stable module-level matcher — tells Quill clipboard how to deserialize <figure> back to a FigureBlot delta op
const _figureMatchers: any[] = [
    ['figure.content-image', (node: HTMLElement) => {
        const Delta = (Quill as any).import('delta');
        return new Delta().insert({image: FigureBlot.value(node)});
    }]
];

interface Props extends FormFieldProps {
    fullEditor?: boolean;
}

const formats = [
    'header',
    'code-block', 'bold', 'italic', 'underline',
    'list', 'bullet', 'indent',
    'link', 'image'
];

interface DialogState {
    open: boolean;
    blot: any;
    value: FigureValue;
}

export const QuillEditorField = (props: Props) => {
    const {name, label, onChange, onBlur, disabled, readonly, fullEditor} = props;

    const dispatch = useAppDispatch();
    const {values, setFieldValue} = useFormikContext();

    const quillEditor = useRef<any>();
    const [dialogState, setDialogState] = useState<DialogState>({
        open: false,
        blot: null,
        value: {src: ''}
    });
    const [isUploading, setIsUploading] = useState(false);

    const handleChange = useCallback((value: string) => {
        setFieldValue(name, value, true);
        if (onChange) {
            onChange(value);
        }
    }, [name, onChange, setFieldValue]);

    const handleBlur = useCallback(() => {
        if (onBlur) {
            onBlur(undefined);
        }
    }, [onBlur]);

    const handlers = useMemo(() => ({
        undo: () => quillEditor.current?.history.undo(),
        redo: () => quillEditor.current?.history.redo(),
    }), []);

    const modules = useMemo(() => ({
        history: {delay: 500, maxStack: 100, userOnly: true},
        toolbar: {
            container: !fullEditor ? [
                [{'header': 2}, {'header': 3}, {'header': ''}],
                ['code-block', 'bold', 'italic', 'underline'],
                [{'list': 'bullet'}, {'list': 'ordered'}/*, {'indent': '-1'}, {'indent': '+1'}*/],
                ['link'],
                ['undo', 'redo']
            ] : [
                [{'header': ['', 1, 2, 3]}],
                ['bold', 'italic', 'underline'],
                [{'list': 'bullet'}, {'list': 'ordered'}/*, {'indent': '-1'}, {'indent': '+1'}*/],
                ['image', 'link'],
                ['undo', 'redo']
            ],
            handlers
        },
        imageUploader: {
            upload: async (file: File): Promise<string> => {
                setIsUploading(true);
                try {
                    const uploaded = await uploadFile(dispatch, file);
                    if (!uploaded?.download_url) throw new Error('Upload failed');
                    return uploaded.download_url;
                } finally {
                    setIsUploading(false);
                }
            }
        },
        clipboard: {matchVisual: false, matchers: _figureMatchers}
    }), [fullEditor, handlers, dispatch]);

    // Click on figure in editor → open dialog
    useEffect(() => {
        const container = quillEditor.current?.root as HTMLElement | undefined;
        if (!container) return;

        const handleClick = (e: MouseEvent) => {
            const figure = (e.target as HTMLElement).closest?.('figure') as HTMLElement | null;
            if (!figure) return;
            const blot = (Quill as any).find(figure);
            if (!blot) return;
            setDialogState({open: true, blot, value: FigureBlot.value(figure)});
        };

        container.addEventListener('click', handleClick);
        return () => container.removeEventListener('click', handleClick);
    });

    const handleDialogConfirm = useCallback((newValue: FigureValue) => {
        const editor = quillEditor.current;
        const blot = dialogState.blot;
        if (!editor || !blot) return;
        const editorScrollTop = editor.root.scrollTop;
        const pageScrollY = window.scrollY;
        const index = editor.getIndex(blot);
        editor.deleteText(index, 1, 'user');
        editor.insertEmbed(index, 'image', newValue, 'user');
        editor.setSelection(index + 1, 0, 'silent');
        editor.root.scrollTop = editorScrollTop;
        window.scrollTo({top: pageScrollY, behavior: 'instant' as ScrollBehavior});
        setDialogState(s => ({...s, open: false}));
    }, [dialogState.blot]);

    useEffect(() => {
        const icons = Quill.import("ui/icons");
        if (icons["undo"]) {
            return;
        }
        icons['header'] = {
            ...icons['header'],
            '3': icons['header']['2'],
            '2': icons['header']['1']
        };
        icons["undo"] = '<svg viewBox="0 0 18 18">' +
            '    <polygon class="ql-fill ql-stroke" points="6 10 4 12 2 10 6 10" />' +
            '    <path' +
            '      class="ql-stroke"' +
            '      d="M8.09,13.91A4.6,4.6,0,0,0,9,14,5,5,0,1,0,4,9"' +
            '    />' +
            '  </svg>';
        icons["redo"] = '<svg viewBox="0 0 18 18">' +
            '    <polygon class="ql-fill ql-stroke" points="12 10 14 12 16 10 12 10" />' +
            '    <path' +
            '      class="ql-stroke"' +
            '      d="M9.91,13.91A4.6,4.6,0,0,1,9,14a5,5,0,1,1,5-5"' +
            '    />' +
            '  </svg>';
    }, []);

    return <Box className={'quill-render'} sx={{
        '& .MuiFormLabel-root': {
            color: 'var(--color-black-text)',
            fontSize: '14px'
        },
        '& .ql-editor': {
            minHeight: '250px',
            maxHeight: 'calc(100vh - 320px)',
            overflow: 'auto',
            backgroundColor: fullEditor ? '#F5F5F5' : undefined,
            '& p': {
                padding: '4px 0'
            },
            opacity: isUploading ? 0.5 : undefined,
        },
    }}>
        <FormLabel>{label}</FormLabel>
        <ReactQuill theme="snow"
            ref={(el) => {
                if (el) {
                    quillEditor.current = el.getEditor();
                }
            }}
            value={(values as any)[name]}
            readOnly={disabled || readonly}
            modules={modules}
            formats={formats}
            onChange={handleChange}
            onBlur={handleBlur}
        />
        <QuillImageDialog
            open={dialogState.open}
            value={dialogState.value}
            onConfirm={handleDialogConfirm}
            onClose={() => setDialogState(s => ({...s, open: false}))}
        />
    </Box>;
}
